// Enemy entities: pooled data, per-kind steering, status effects, and separation.
// Steering is cheap (direct seek + a few behavior variants); separation uses the
// spatial hash to keep chasers from stacking into one overlapping column (plan §9-F).

import { Pool } from '../core/pool';
import type { SpatialHash } from '../core/spatialHash';
import { CHARGE, ENEMY_SHOT, MAX_ENEMY_RADIUS, POOL_CAP, SEPARATION, SUMMON, WEAVE, WET } from './balance';
import type { EnemyKind, EnemyType } from './balance';

/** Mortar preferred standoff distance from the player. */
const RANGED_STANDOFF = 300;

export interface Enemy {
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  hp: number;
  maxHp: number;
  speedPerSec: number;
  radius: number;
  contactDmg: number;
  xp: number;
  color: number;
  hitFlashSec: number;
  typeKey: string;
  kind: EnemyKind;
  sprite: string;
  // Status effects.
  slowSec: number;
  slowMul: number;
  burnSec: number;
  burnDps: number;
  slowTint: number;
  burnTint: number;
  sporeMarkedSec: number;
  blightSpreadCd: number;
  wetSec: number; // slowed by a water puddle (lingers after leaving)
  confusedSec: number; // hidden variant (Tâmpești): wanders instead of chasing
  wanderAng: number; // current wander heading while confused
  // Hit bookkeeping (avoid double-hits without per-frame allocation).
  lastProjHit: number;
  chainMark: number;
  orbitHitCd: number;
  // Behavior state.
  wavePhase: number; // weave
  chargeCd: number; // charge: time until next charge
  windupSec: number; // charge: telegraph remaining
  dashSec: number; // charge: dash remaining
  dashX: number;
  dashY: number;
  splitCount: number; // spawns this many slimes on death
  isBoss: boolean;
  summonCd: number; // flask boss: time until next summon
  fireCd: number; // ranged enemy: time until next shot
  bloomCd: number; // rooted/verdant enemies: time until their next terrain pulse
  railCd: number; // Starfall emitters: time until their next directional rail pulse
  rangedChases: boolean;
}

function makeEnemy(): Enemy {
  return {
    x: 0, y: 0, prevX: 0, prevY: 0,
    hp: 1, maxHp: 1, speedPerSec: 0, radius: 1, contactDmg: 0, xp: 0,
    color: 0xffffff, hitFlashSec: 0, typeKey: 'slime', kind: 'chase', sprite: 'slime',
    slowSec: 0, slowMul: 1, burnSec: 0, burnDps: 0,
    slowTint: 0, burnTint: 0, sporeMarkedSec: 0, blightSpreadCd: 0,
    wetSec: 0, confusedSec: 0, wanderAng: 0,
    lastProjHit: -1, chainMark: -1, orbitHitCd: 0,
    wavePhase: 0, chargeCd: 0, windupSec: 0, dashSec: 0, dashX: 0, dashY: 0, splitCount: 0,
    isBoss: false, summonCd: 0, fireCd: 0, bloomCd: 0, railCd: 0, rangedChases: false,
  };
}

export const createEnemyPool = (): Pool<Enemy> => new Pool(POOL_CAP.enemies, makeEnemy);

/** Spawn one enemy (returns it so callers can vary behavior phase). */
export function spawnEnemy(
  pool: Pool<Enemy>,
  type: EnemyType,
  x: number,
  y: number,
  hpScale = 1,
  dmgScale = 1,
  r01 = 0,
): Enemy | null {
  const e = pool.acquire();
  if (e === null) return null;
  e.x = x;
  e.y = y;
  e.prevX = x;
  e.prevY = y;
  e.hp = type.hp * hpScale;
  e.maxHp = e.hp;
  e.speedPerSec = type.speedPerSec;
  e.radius = type.radius;
  e.contactDmg = type.contactDmg * dmgScale;
  e.xp = type.xp;
  e.color = type.color;
  e.typeKey = type.key;
  e.kind = type.kind;
  e.sprite = type.sprite;
  e.hitFlashSec = 0;
  e.slowSec = 0;
  e.slowMul = 1;
  e.burnSec = 0;
  e.burnDps = 0;
  e.slowTint = 0;
  e.burnTint = 0;
  e.sporeMarkedSec = 0;
  e.blightSpreadCd = 0;
  e.wetSec = 0;
  e.confusedSec = 0;
  e.wanderAng = 0;
  e.lastProjHit = -1;
  e.chainMark = -1;
  e.orbitHitCd = 0;
  e.wavePhase = r01 * Math.PI * 2;
  e.chargeCd = CHARGE.cooldownSec * (0.5 + r01);
  e.windupSec = 0;
  e.dashSec = 0;
  e.splitCount = type.split?.count ?? 0;
  e.isBoss = false;
  e.summonCd = type.summon?.cooldownSec ?? SUMMON.cooldownSec;
  e.fireCd = ENEMY_SHOT.cooldownSec * (0.5 + r01);
  e.bloomCd = (type.bloomEmitter?.cooldownSec ?? 0) * (0.7 + r01 * 0.3);
  e.railCd = (type.railEmitter?.cooldownSec ?? 0) * (0.7 + r01 * 0.3);
  e.rangedChases = type.rangedAttack?.chaseWhileFiring === true || type.sprite === 'spore';
  return e;
}

/** Spawn a boss (big, high HP, drops a chest on death - handled by the world). */
export function spawnBoss(
  pool: Pool<Enemy>,
  type: EnemyType,
  x: number,
  y: number,
  hpScale = 1,
): Enemy | null {
  const e = spawnEnemy(pool, type, x, y, hpScale, 1, 0.5);
  if (e !== null) e.isBoss = true;
  return e;
}

/** Advance every active enemy: status ticks, then per-kind movement. */
export function updateEnemies(pool: Pool<Enemy>, dt: number, px: number, py: number): void {
  const items = pool.items;
  for (let i = 0; i < pool.count; i++) {
    const e = items[i];

    // Record previous position for render interpolation (must run every tick).
    e.prevX = e.x;
    e.prevY = e.y;

    if (e.hitFlashSec > 0) e.hitFlashSec -= dt;
    if (e.orbitHitCd > 0) e.orbitHitCd -= dt;
    if (e.burnSec > 0) {
      e.hp -= e.burnDps * dt;
      e.burnSec -= dt;
    }
    let speed = e.speedPerSec;
    if (e.slowSec > 0) {
      speed *= e.slowMul;
      e.slowSec -= dt;
    }
    if (e.wetSec > 0) {
      speed *= WET.slowMul;
      e.wetSec -= dt;
    }

    // Confused (hidden variant, Tâmpești): stagger around a drifting heading instead of
    // chasing. Only ever set on /hidden, so this branch is dead on the base game.
    if (e.confusedSec > 0) {
      e.confusedSec -= dt;
      e.wavePhase += dt * 6;
      const ang = e.wanderAng + Math.sin(e.wavePhase) * 1.5;
      e.x += Math.cos(ang) * speed * 0.6 * dt;
      e.y += Math.sin(ang) * speed * 0.6 * dt;
      continue;
    }

    const dx = px - e.x;
    const dy = py - e.y;
    const d = Math.sqrt(dx * dx + dy * dy) || 1;

    if (e.kind === 'stationary') {
      continue;
    }
    if (e.kind === 'charge') {
      moveCharge(e, dt, dx, dy, d, speed);
    } else if (e.kind === 'weave') {
      e.wavePhase += WEAVE.freq * dt;
      const nx = dx / d;
      const ny = dy / d;
      const wob = Math.sin(e.wavePhase) * WEAVE.ampPerSec * dt;
      e.x += nx * speed * dt - ny * wob;
      e.y += ny * speed * dt + nx * wob;
    } else if (e.kind === 'ranged') {
      if (e.rangedChases) {
        // Spor Sayer (hidden) chases while lobbing notes - it never backs off, even when the
        // player closes in.
        const step = (speed * dt) / d;
        e.x += dx * step;
        e.y += dy * step;
      } else {
        // Mortar: maintain a standoff (approach if too far, back off if too close).
        const dir = d > RANGED_STANDOFF + 30 ? 1 : d < RANGED_STANDOFF - 30 ? -1 : 0;
        if (dir !== 0) {
          const step = (dir * speed * dt) / d;
          e.x += dx * step;
          e.y += dy * step;
        }
      }
    } else {
      // chase / split / summon
      const step = (speed * dt) / d;
      e.x += dx * step;
      e.y += dy * step;
    }
  }
}

function moveCharge(e: Enemy, dt: number, dx: number, dy: number, d: number, speed: number): void {
  if (e.dashSec > 0) {
    // Locked-direction dash.
    e.dashSec -= dt;
    e.x += e.dashX * speed * CHARGE.dashSpeedMul * dt;
    e.y += e.dashY * speed * CHARGE.dashSpeedMul * dt;
    return;
  }
  if (e.windupSec > 0) {
    // Telegraph: hold position, lock the dash direction at the end.
    e.windupSec -= dt;
    if (e.windupSec <= 0) {
      e.dashSec = CHARGE.dashSec;
      e.dashX = dx / d;
      e.dashY = dy / d;
    }
    return;
  }
  // Normal chase; trigger a charge when off cooldown and in range.
  e.chargeCd -= dt;
  if (e.chargeCd <= 0 && d < CHARGE.triggerRange) {
    e.windupSec = CHARGE.windupSec;
    e.chargeCd = CHARGE.cooldownSec;
    return;
  }
  const step = (speed * dt) / d;
  e.x += dx * step;
  e.y += dy * step;
}

/** Push overlapping enemies apart so chasers don't merge into one blob. */
export function separateEnemies(pool: Pool<Enemy>, hash: SpatialHash, buf: number[]): void {
  const items = pool.items;
  for (let i = 0; i < pool.count; i++) {
    const e = items[i];
    const n = hash.query(e.x, e.y, e.radius + MAX_ENEMY_RADIUS, buf);
    let pushX = 0;
    let pushY = 0;
    for (let j = 0; j < n; j++) {
      const idx = buf[j];
      if (idx === i) continue;
      const o = items[idx];
      const dx = e.x - o.x;
      const dy = e.y - o.y;
      const minD = e.radius + o.radius;
      const d2 = dx * dx + dy * dy;
      if (d2 > 0.0001 && d2 < minD * minD) {
        const d = Math.sqrt(d2);
        const overlap = (minD - d) / d;
        pushX += dx * overlap;
        pushY += dy * overlap;
      }
    }
    // Normalize + cap the per-tick step so dense crowds settle instead of exploding.
    const mag = Math.sqrt(pushX * pushX + pushY * pushY);
    if (mag > 0.0001) {
      const step = Math.min(mag * SEPARATION.strength, SEPARATION.maxStep);
      e.x += (pushX / mag) * step;
      e.y += (pushY / mag) * step;
    }
  }
}
